import { motion } from "framer-motion";
import {
    Flame,
    Crosshair,
    Dumbbell,
    ScrollText,
    Star,
    Target,
    Wind,
    Flower2,
    UserCheck,
    BadgeCheck,
} from "lucide-react";
import Mandala from "./Mandala";
import { Eyebrow, Reveal, EASE } from "./Reveal";
import { IMG } from "../site";

const PHILOSOPHY = [
    { icon: Flame, label: "Advanced Asanas" },
    { icon: Crosshair, label: "Perfect Alignment" },
    { icon: Dumbbell, label: "Strength & Flexibility" },
    { icon: ScrollText, label: "Competition Rules" },
    { icon: Star, label: "Scoring & Presentation" },
    { icon: Target, label: "Mental Focus" },
    { icon: Wind, label: "Breathing" },
    { icon: Flower2, label: "Meditation" },
    { icon: UserCheck, label: "Personalized Tournament Coaching" },
];

export default function About() {
    return (
        <section id="about" data-testid="about-section" className="relative py-20 sm:py-28 lg:py-32">
            <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
                <Eyebrow>The Coach</Eyebrow>
                <Reveal>
                    <h2 className="mt-6 max-w-3xl font-display text-4xl leading-[1.02] tracking-wide sm:text-5xl lg:text-6xl">
                        TRAIN WITH PURPOSE.{" "}
                        <span className="text-gold-grad">PERFORM WITH PRECISION.</span>
                    </h2>
                </Reveal>

                <div className="mt-14 grid items-start gap-14 lg:grid-cols-[1.15fr_0.85fr]">
                    <div>
                        <Reveal>
                            <p className="max-w-2xl text-[15px] leading-relaxed text-[#B8B8B8] sm:text-base">
                                Trainer Aryan is a certified National Yoga Coach focused on
                                advanced yogasana, competition preparation, performance
                                refinement and personalized coaching — built for athletes
                                who train to win.
                            </p>
                            <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.3em] text-[#E88916]">
                                The Coaching Philosophy
                            </p>
                        </Reveal>

                        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {PHILOSOPHY.map(({ icon: Icon, label }, i) => (
                                <motion.div
                                    key={label}
                                    data-testid="philosophy-chip"
                                    className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-3.5 transition-all duration-300 hover:border-[#D4A017]/40 hover:bg-[#D4A017]/[0.06]"
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    transition={{ duration: 0.6, delay: i * 0.05, ease: EASE }}
                                >
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#D4A017]/35">
                                        <Icon size={15} className="text-[#F5C542]" />
                                    </span>
                                    <span className="text-xs font-semibold uppercase tracking-[0.12em] text-white/85">
                                        {label}
                                    </span>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    {/* profile card */}
                    <Reveal delay={0.15} className="relative">
                        <div className="absolute -right-2 -top-6 -z-10 h-56 w-56 opacity-30 sm:-right-8 sm:h-72 sm:w-72">
                            <Mandala className="h-full w-full animate-spin-slower" petals={12} inner={8} />
                        </div>
                        <div className="gold-frame rounded-b-3xl rounded-t-[999px] p-[3px]">
                            <div className="relative overflow-hidden rounded-b-3xl rounded-t-[999px]">
                                <img
                                    src={IMG.profile}
                                    alt="Trainer Aryan in an advanced seated backbend asana"
                                    className="aspect-[4/5] w-full object-cover grayscale contrast-110"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                            </div>
                        </div>

                        <div
                            data-testid="coach-profile-card"
                            className="glass -mt-16 ml-4 mr-[-1rem] rounded-2xl p-6 sm:-mr-6"
                        >
                            <div className="flex items-center gap-3">
                                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#F5C542] to-[#B8860B]">
                                    <BadgeCheck size={18} className="text-black" />
                                </span>
                                <div>
                                    <p className="font-display text-2xl tracking-[0.14em] text-white">
                                        TRAINER ARYAN
                                    </p>
                                    <p className="text-[10px] font-bold uppercase tracking-[0.26em] text-[#F5C542]">
                                        Certified National Yoga Coach
                                    </p>
                                </div>
                            </div>
                            <div className="my-5 h-px bg-gradient-to-r from-[#D4A017]/50 via-[#D4A017]/20 to-transparent" />
                            <p className="text-xs leading-relaxed tracking-wide text-[#B8B8B8]">
                                Advanced Yogasana · Competition Coaching · Performance
                                Refinement — training athletes for the podium.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
